import React from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {
    Box,
    Flex,
    Text,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'

/**
 * Renders the folder navigation for the wishlist page.
 * Desktop: vertical sidebar with "Collections" title.
 * Mobile: CSS-only horizontal scroll strip, no scrollbar, active underline.
 */
const WishlistFolderNav = ({
    wishList,
    customFolders,
    activeFolderId
}) => {
    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig('WishlistFolderNav', {})

    const myFavouritesLabel = formatMessage({
        id: 'wishlist.folder.my_favourites',
        defaultMessage: 'My Favourites'
    })

    const myFavouritesId = wishList?.id
    const myFavouritesCount = wishList?.customerProductListItems?.length ?? 0
    const isMyFavouritesActive = !activeFolderId || activeFolderId === myFavouritesId

    const folders = [
        {id: myFavouritesId, name: myFavouritesLabel, count: myFavouritesCount, to: '/wishlist', isActive: isMyFavouritesActive},
        ...(customFolders ?? []).map((f) => ({
            id: f.id,
            name: f.name,
            count: f.customerProductListItems?.length ?? 0,
            to: `/wishlist/folder/${f.id}`,
            isActive: activeFolderId === f.id
        }))
    ]

    return (
        <Box sx={styles.container} data-testid="wishlist-folder-nav" w="100%" minW={0}>
            {/* ---- Desktop: Collections title + vertical sidebar list ---- */}
            <Box display={{base: 'none', lg: 'block'}}>
                <Text sx={styles.sidebarTitle}>
                    <FormattedMessage id="wishlist.collections.title" defaultMessage="Collections" />
                </Text>
                <Flex sx={styles.folderList}>
                    {folders.map((folder) => (
                        <Box
                            key={folder.id}
                            as={Link}
                            to={folder.to}
                            useNavLink
                            sx={{
                                ...styles.folderItem,
                                ...(folder.isActive ? styles.folderItemActive : {})
                            }}
                            data-testid="wishlist-folder-nav-item"
                        >
                            <Text
                                as="span"
                                textStyle={folder.isActive ? 'bodyMdBold' : 'bodyMdRegular'}
                                sx={styles.folderName}
                                color={folder.isActive ? 'webTypo.textPrimary' : 'webTypo.textSecondary'}
                                noOfLines={1}
                                overflow="hidden"
                                textOverflow="ellipsis"
                                whiteSpace="nowrap"
                                maxWidth="100%"
                            >
                                {folder.name}
                            </Text>
                            <Text as="span" sx={styles.folderCount} textStyle={folder.isActive ? 'bodyMdBold' : 'bodyMdRegular'} color={folder.isActive ? 'webTypo.textPrimary' : 'webTypo.textSecondary'}>
                                &nbsp;({folder.count})
                            </Text>
                        </Box>
                    ))}
                </Flex>
            </Box>

            {/* ---- Mobile: CSS-only horizontal scroll, no scrollbar ---- */}
            <Box sx={styles.mobileNavContainer}>
                <Box sx={styles.mobileScrollContainer}>
                    {folders.map((folder) => (
                        <Box
                            key={folder.id}
                            as={Link}
                            to={folder.to}
                            useNavLink
                            sx={{
                                ...styles.mobileFolderItem,
                                ...(folder.isActive ? styles.mobileFolderItemActive : {})
                            }}
                            data-testid="wishlist-folder-nav-item"
                        >
                            {folder.name}
                            <Text as="span" sx={styles.folderCount}>
                                &nbsp;({folder.count})
                            </Text>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    )
}

WishlistFolderNav.propTypes = {
    wishList: PropTypes.object,
    customFolders: PropTypes.array,
    activeFolderId: PropTypes.string
}

WishlistFolderNav.defaultProps = {
    customFolders: []
}

export default WishlistFolderNav

